<template>
  <article class="pc" :class="{ open: isOpen, off: !local.enabled }">
    <div class="pc-bar" @click="toggleOpen">
      <div class="pc-bar-main">
        <div class="pc-name"><span class="agent">{{ local.name }}</span></div>
        <div class="pc-desc">{{ local.description }}</div>
      </div>

      <div class="pc-right" @click.stop>
        <span class="prov-badge">
          <span class="prov-dot" :style="{ background: meta.dot }"></span>
          {{ meta.label }}
          <span class="prov-model">{{ local.model }}</span>
        </span>

        <label class="sw" v-if="local.allowDisabled">
          <input type="checkbox" v-model="local.enabled" @change="onToggle" />
          <span class="track"></span><span class="knob"></span>
        </label>

        <span class="pc-chev" @click="toggleOpen">▾</span>
      </div>
    </div>

    <div v-show="isOpen" class="pc-body">
      <div class="row mb-3">
        <div class="col-12 col-md-3 mb-3 mb-md-0">
          <label class="fld-label">Provider IA</label>
          <select class="form-control fld" v-model="local.provider" @change="onProviderChange">
            <option v-for="p in providers" :key="p.value" :value="p.value">{{ p.label }}</option>
          </select>
        </div>
        <div class="col-12 col-md-3 mb-3 mb-md-0">
          <label class="fld-label">Modèle</label>
          <select class="form-control fld" v-model="local.model">
            <option v-for="m in availableModels" :key="m" :value="m">{{ m }}</option>
          </select>
        </div>
        <div class="col-12 col-md-2 mb-3 mb-md-0">
          <label class="fld-label">Langue par défaut</label>
          <select class="form-control fld" v-model="local.default_lang">
            <option v-for="l in langs" :key="l" :value="l">{{ l }}</option>
          </select>
        </div>
        <div class="col-12 col-md-4">
          <label class="fld-label">Clé API</label>
          <div class="api-key-field">
            <input
              ref="apiKeyInput"
              class="form-control fld mono"
              :type="apiKeyInputType"
              :value="apiKeyInputValue"
              :placeholder="apiKeyPlaceholder"
              :readonly="showStoredKeyMask"
              @input="onApiKeyInput"
            />
            <button
              v-if="showStoredKeyMask"
              type="button"
              class="api-key-eye"
              :disabled="loadingApiKey"
              :aria-label="apiKeyRevealed ? 'Masquer la clé API' : 'Afficher la clé API'"
              @click="toggleApiKeyVisibility"
            >{{ loadingApiKey ? '...' : (apiKeyRevealed ? '◉' : '◌') }}</button>
          </div>
          <button
            v-if="showStoredKeyMask"
            type="button"
            class="api-key-replace"
            @click="startApiKeyReplacement"
          >Remplacer la clé</button>
        </div>
      </div>

      <div class="var-tool mb-3">
        <div class="vt-field">
          <label class="fld-label">Insérer une variable</label>
          <select class="form-control fld" v-model="selectedVar">
            <option value="" disabled>Choisir une variable...</option>
            <optgroup v-for="g in variableGroups" :key="g.category" :label="g.category">
              <option v-for="v in g.vars" :key="v" :value="v">[{{ v }}]</option>
            </optgroup>
          </select>
        </div>
        <button type="button" class="btn btn-indigo" :disabled="!selectedVar" @click="insertVar">Insérer au curseur</button>
        <p class="var-hint">S'insère à la position du curseur dans le prompt de la langue active, sous la forme [nom_variable].</p>
      </div>

      <div v-if="local.variants.length > 1" class="variant-row">
        <label class="fld-label">Type de prompt</label>
        <select class="form-control fld" v-model="activeVariantKey">
          <option v-for="variant in local.variants" :key="variant.key" :value="variant.key">{{ variant.name }}</option>
        </select>
      </div>

      <div class="lang-row">
        <span class="lang-lbl">Langue du prompt</span>
        <button
          v-for="l in langs"
          :key="l"
          type="button"
          class="lang-pill"
          :class="{ active: activeLang === l, 'has-content': !!activeVariant.prompts[l] }"
          @click="switchLang(l)"
        >
          {{ l }}
          <span class="filled" title="Prompt rempli"></span>
        </button>
      </div>

      <div class="prompt-label">
        <span>Prompt - {{ activeLang }}</span>
        <span class="char">{{ currentPrompt.length }} caractères</span>
      </div>
      <textarea
        ref="promptArea"
        class="prompt-area"
        :value="currentPrompt"
        @input="onPromptInput"
        :placeholder="`Prompt en ${activeLang}`"
      ></textarea>

      <div class="pc-actions">
        <button type="button" class="btn btn-ghost" @click="reset">Réinitialiser</button>
        <button type="button" class="btn btn-ghost" :disabled="saving || previewing" @click="preview">
          {{ previewing ? 'Prévisualisation...' : 'Prévisualiser' }}
        </button>
        <span v-if="saving" class="save-spinner" aria-label="Enregistrement en cours"></span>
        <button type="button" class="btn btn-indigo" :disabled="saving" @click="save">
          {{ saving ? 'Enregistrement...' : (saved ? 'Enregistré' : 'Enregistrer') }}
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import { PROVIDERS, VARIABLE_GROUPS, LANGS, providerMeta, modelsFor } from './promptConfig.js'
import { promptsApi } from '@/_services'

function clonePrompt(prompt) {
  const copy = JSON.parse(JSON.stringify(prompt || {}))
  if (!Array.isArray(copy.variants)) {
    copy.variants = [{ key: copy.key || 'prompt', name: 'Prompt', prompts: copy.prompts || {} }]
  }
  copy.variants.forEach((variant) => {
    if (!variant.prompts) variant.prompts = {}
    LANGS.forEach((lang) => {
      if (variant.prompts[lang] === undefined) variant.prompts[lang] = ''
    })
  })
  if (!copy.default_lang) copy.default_lang = 'FR'
  if (copy.apiKey === undefined) copy.apiKey = ''
  return copy
}

export default {
  name: 'PromptCard',
  props: {
    prompt: { type: Object, required: true },
    open: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    saved: { type: Boolean, default: false },
    previewing: { type: Boolean, default: false },
  },
  data() {
    const local = clonePrompt(this.prompt)
    return {
      providers: PROVIDERS,
      variableGroups: VARIABLE_GROUPS,
      langs: LANGS,
      local,
      isOpen: this.open,
      activeLang: local.default_lang || 'FR',
      activeVariantKey: local.variants[0].key,
      selectedVar: '',
      apiKeyRevealed: false,
      revealedApiKey: '',
      loadingApiKey: false,
      replacingApiKey: false,
    }
  },
  computed: {
    meta() {
      return providerMeta(this.local.provider)
    },
    availableModels() {
      return modelsFor(this.local.provider)
    },
    currentPrompt() {
      return this.activeVariant.prompts[this.activeLang] || ''
    },
    activeVariant() {
      return this.local.variants.find(variant => variant.key === this.activeVariantKey) || this.local.variants[0]
    },
    showStoredKeyMask() {
      return this.local.has_key && !this.local.apiKey && !this.replacingApiKey
    },
    apiKeyInputType() {
      if (this.showStoredKeyMask) return 'text'
      return 'password'
    },
    apiKeyInputValue() {
      if (this.showStoredKeyMask) {
        return this.apiKeyRevealed ? this.revealedApiKey : this.fullApiKeyMask
      }
      return this.local.apiKey
    },
    partialApiKeyMask() {
      return this.local.api_key_mask || ''
    },
    fullApiKeyMask() {
      if (this.local.api_key_mask_full) return this.local.api_key_mask_full
      if (!this.local.api_key_mask) return ''
      return '*'.repeat(this.local.api_key_mask.length)
    },
    apiKeyPlaceholder() {
      if (!this.local.has_key) return 'Coller la clé API'
      return ''
    },
  },
  watch: {
    prompt: {
      deep: true,
      handler(nextPrompt) {
        this.local = clonePrompt(nextPrompt)
        this.activeVariantKey = this.local.variants[0].key
        this.apiKeyRevealed = false
        this.revealedApiKey = ''
        this.loadingApiKey = false
        this.replacingApiKey = false
        if (!this.langs.includes(this.activeLang)) {
          this.activeLang = this.local.default_lang || 'FR'
        }
      },
    },
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen
    },
    onProviderChange() {
      if (!this.availableModels.includes(this.local.model)) {
        this.local.model = this.availableModels[0] || ''
      }
    },
    onToggle() {
      this.$emit('toggle', { id: this.local.id, enabled: this.local.enabled })
    },
    switchLang(lang) {
      this.activeLang = lang
    },
    onPromptInput(event) {
      this.$set(this.activeVariant.prompts, this.activeLang, event.target.value)
    },
    onApiKeyInput(event) {
      this.local.apiKey = event.target.value
    },
    toggleApiKeyVisibility() {
      if (this.apiKeyRevealed) {
        this.apiKeyRevealed = false
        return
      }
      if (this.revealedApiKey) {
        this.apiKeyRevealed = true
        return
      }

      this.loadingApiKey = true
      promptsApi.getApiKey(this.local.id)
        .then((result) => {
          this.revealedApiKey = result && result.api_key ? result.api_key : ''
          this.apiKeyRevealed = !!this.revealedApiKey
        })
        .catch(() => {
          this.$swal({
            type: 'error',
            title: 'Erreur',
            text: "Impossible de charger la clé API.",
          })
        })
        .then(() => {
          this.loadingApiKey = false
        })
    },
    startApiKeyReplacement() {
      this.apiKeyRevealed = false
      this.revealedApiKey = ''
      this.replacingApiKey = true
      this.local.apiKey = ''
      this.$nextTick(() => this.$refs.apiKeyInput && this.$refs.apiKeyInput.focus())
    },
    insertVar() {
      if (!this.selectedVar) return
      const token = `[${this.selectedVar}]`
      const textarea = this.$refs.promptArea
      const start = textarea && textarea.selectionStart != null ? textarea.selectionStart : this.currentPrompt.length
      const end = textarea && textarea.selectionEnd != null ? textarea.selectionEnd : this.currentPrompt.length
      const value = this.currentPrompt
      this.$set(this.activeVariant.prompts, this.activeLang, value.slice(0, start) + token + value.slice(end))
      this.$nextTick(() => {
        if (!textarea) return
        const pos = start + token.length
        textarea.focus()
        textarea.setSelectionRange(pos, pos)
      })
      this.selectedVar = ''
    },
    save() {
      this.$emit('save', JSON.parse(JSON.stringify(this.local)))
    },
    preview() {
      this.$emit('preview', {
        id: this.local.id,
        content: this.currentPrompt,
      })
    },
    reset() {
      this.local = clonePrompt(this.prompt)
      this.activeLang = this.local.default_lang || 'FR'
      this.activeVariantKey = this.local.variants[0].key
      this.apiKeyRevealed = false
      this.revealedApiKey = ''
      this.loadingApiKey = false
      this.replacingApiKey = false
    },
  },
}
</script>

<style scoped>
.pc { background: #fff; border: 1px solid #E9E7E0; border-radius: 14px; box-shadow: 0 1px 2px rgba(20,20,30,.04), 0 8px 24px rgba(20,20,30,.05); overflow: hidden; }
.pc.off { opacity: .62; }
.pc-bar { display: flex; align-items: center; gap: 14px; padding: 14px 18px; cursor: pointer; }
.pc-bar-main { min-width: 0; }
.pc-name { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 18px; letter-spacing: .3px; }
.pc-name .agent { color: #3A33C7; }
.pc-desc { color: #56565E; font-size: 12.5px; }
.pc-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.prov-badge { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 600; padding: 5px 10px; border-radius: 8px; border: 1px solid #DAD7CE; background: #fff; white-space: nowrap; }
.prov-dot { width: 8px; height: 8px; border-radius: 50%; }
.prov-model { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #56565E; }
.sw { position: relative; width: 40px; height: 23px; flex: none; margin: 0; }
.sw input { opacity: 0; width: 0; height: 0; }
.sw .track { position: absolute; inset: 0; background: #CFCDC4; border-radius: 999px; transition: .18s; }
.sw .knob { position: absolute; top: 3px; left: 3px; width: 17px; height: 17px; background: #fff; border-radius: 50%; transition: .18s; box-shadow: 0 1px 2px rgba(0,0,0,.25); }
.sw input:checked + .track { background: #1FA971; }
.sw input:checked + .track + .knob { transform: translateX(17px); }
.pc-chev { color: #56565E; transition: transform .2s; }
.pc.open .pc-chev { transform: rotate(180deg); }
.pc-body { border-top: 1px solid #E9E7E0; padding: 18px; background: #FCFBF8; }
.fld-label { display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; color: #56565E; margin-bottom: 6px; }
.fld { border: 1px solid #DAD7CE; border-radius: 9px; font-size: 13px; color: #16161A; }
.fld:focus { border-color: #4F46E5; box-shadow: 0 0 0 3px #EEEDFB; }
.mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; }
.api-key-field { position: relative; }
.api-key-field .form-control { padding-right: 42px; }
.api-key-eye { position: absolute; top: 50%; right: 8px; transform: translateY(-50%); border: 0; background: transparent; color: #56565E; font-size: 18px; line-height: 1; cursor: pointer; }
.api-key-eye:disabled { cursor: wait; }
.api-key-replace { border: 0; background: transparent; color: #3A33C7; font-size: 11px; font-weight: 600; padding: 4px 0 0; cursor: pointer; }
.api-key-replace:hover { text-decoration: underline; }
.var-tool { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; padding: 12px; border: 1px dashed #DAD7CE; border-radius: 10px; background: #fff; }
.vt-field { flex: 1; min-width: 220px; }
.var-hint { font-size: 11.5px; color: #56565E; width: 100%; margin: 0; }
.variant-row { max-width: 420px; margin-bottom: 14px; }
.lang-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.lang-lbl { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; color: #56565E; margin-right: 2px; }
.lang-pill { position: relative; font-family: 'Inter', sans-serif; font-weight: 600; font-size: 12px; padding: 6px 13px; border-radius: 8px; border: 1px solid #DAD7CE; background: #fff; color: #56565E; cursor: pointer; transition: .15s; }
.lang-pill:hover { border-color: #4F46E5; color: #3A33C7; }
.lang-pill.active { background: #4F46E5; border-color: #4F46E5; color: #fff; }
.lang-pill .filled { position: absolute; top: -3px; right: -3px; width: 8px; height: 8px; border-radius: 50%; background: #1FA971; border: 1.5px solid #fff; display: none; }
.lang-pill.has-content .filled { display: block; }
.lang-pill.active .filled { border-color: #4F46E5; }
.prompt-label { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.prompt-label span:first-child { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; color: #56565E; }
.char { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: #56565E; }
.prompt-area { width: 100%; min-height: 200px; resize: vertical; padding: 14px; border: 1px solid #DAD7CE; border-radius: 11px; font-family: 'JetBrains Mono', monospace; font-size: 12.5px; line-height: 1.65; color: #16161A; background: #fff; outline: none; }
.prompt-area:focus { border-color: #4F46E5; box-shadow: 0 0 0 3px #EEEDFB; }
.pc-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
.save-spinner { width: 18px; height: 18px; margin: 9px 2px; border: 2px solid #DAD7CE; border-top-color: #4F46E5; border-radius: 50%; animation: save-spin .7s linear infinite; }
.btn-indigo { background: #4F46E5; color: #fff; border: none; font-weight: 600; }
.btn-indigo:hover { background: #3A33C7; color: #fff; }
.btn-indigo:disabled { opacity: .5; }
.btn-ghost { background: #fff; border: 1px solid #DAD7CE; color: #16161A; font-weight: 600; }
.btn-ghost:hover { border-color: #4F46E5; color: #3A33C7; }
@media (max-width: 767px) {
  .pc-bar { align-items: flex-start; flex-direction: column; }
  .pc-right { margin-left: 0; width: 100%; justify-content: space-between; }
}
@keyframes save-spin { to { transform: rotate(360deg); } }
</style>
